import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Alert, Table, Tooltip, Tag, Select, DatePicker,
} from 'antd';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FormInput } from '../../../components';

const buildErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  const code = error?.code ? `(${error.code}) ` : '';
  const message = error?.message || '';
  const joined = `${message || code}`.trim();
  return joined || 'Unknown error';
};

const mapFieldPathToColumnKey = (fieldPath) => {
  const normalized = (fieldPath || '')
    .toString()
    .replace(/^orders\[\d+]\./, '');

  if (!normalized) return null;
  if (normalized.startsWith('internalId') || normalized.startsWith('orderNumber')) return 'internalId';
  if (normalized.startsWith('startDate')) return 'startDate';
  if (normalized.startsWith('endDate')) return 'endDate';
  if (normalized.startsWith('vehicles') || normalized.startsWith('vehicle')) return 'vehicles';
  if (normalized.startsWith('stops')) return 'stops';
  if (normalized.startsWith('action')) return 'action';
  if (normalized.startsWith('type')) return 'type';
  if (normalized.startsWith('description')) return 'description';
  return null;
};

const actionTag = (t, action) => {
  const normalized = (action || '').toString().toUpperCase();
  if (normalized === 'UPDATE') {
    return <Tag color="gold">{t('ORDER.IMPORT_ORDER.ACTION_UPDATE')}</Tag>;
  }
  if (normalized === 'CREATE') {
    return <Tag color="green">{t('ORDER.IMPORT_ORDER.ACTION_CREATE')}</Tag>;
  }
  return <span>—</span>;
};

const ImportOrderPreviewStep = ({
  previewState,
  showAiVehiclesTooltip,
}) => {
  const { t } = useTranslation();

  const {
    errorsByIndex,
    fileLevelErrors,
    editedPreview,
    allVehicles,
    vehiclesLoading,
    tableData,
    page,
    pageSize,
    selectedRowKeys,
    hasUnvalidatedChanges,
    handleFieldChange,
    handleTableChange,
    setSelectedRowKeys,
  } = previewState;

  const rowErrorFields = useMemo(() => {
    const map = new Map();
    errorsByIndex.forEach((errs, rowIdx) => {
      const fields = new Set();
      errs.forEach((error) => {
        const key = mapFieldPathToColumnKey(error?.fieldPath);
        if (key) fields.add(key);
      });
      map.set(rowIdx, fields);
    });
    return map;
  }, [errorsByIndex]);

  const hasFieldError = useCallback(
    (rowIndex, field) => rowErrorFields.get(rowIndex)?.has(field),
    [rowErrorFields],
  );

  const columns = useMemo(() => ([
    {
      title: t('ORDER.IMPORT_ORDER.ACTION'),
      dataIndex: 'action',
      key: 'action',
      width: 88,
      render: (val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        const className = hasFieldError(rowIndex, 'action') ? 'aiio-field-error' : '';
        return <span className={className}>{actionTag(t, editedPreview[rowIndex]?.action ?? val)}</span>;
      },
    },
    {
      title: t('ORDER.IMPORT_ORDER.INTERNAL_ID'),
      dataIndex: 'internalId',
      key: 'internalId',
      width: 180,
      render: (val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        const action = (editedPreview[rowIndex]?.action ?? record?.action ?? '').toString().toUpperCase();
        const showOverwriteInfo = action === 'UPDATE';
        return (
          <div className="aiio-flex-row aiio-align-center aiio-gap-8">
            <div className="aiio-cell-compact">
              <FormInput
                value={(editedPreview[rowIndex]?.internalId) ?? val}
                placeholder={t('ORDER.TRANSPORT_ORDER_NUMBER_PROMPT')}
                status={hasFieldError(rowIndex, 'internalId') ? 'error' : undefined}
                onChange={(e) => handleFieldChange(rowIndex, 'internalId', e?.target?.value)}
              />
            </div>
            {showOverwriteInfo ? (
              <Tooltip title={t('ORDER.IMPORT_ORDER.ORDER_EXISTS_OVERWRITE_WARNING')}>
                <WarningOutlined className="aiio-warning-icon" />
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      title: t('COMMON.ERRORS'),
      key: 'errors',
      align: 'center',
      width: 56,
      render: (_val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        const errs = errorsByIndex.get(rowIndex) || [];
        const count = errs.length;
        if (!count) {
          return (
            <Tag color="green">0</Tag>
          );
        }
        return (
          <Tooltip
            title={(
              <div className="aiio-errors-tooltip">
                {errs.map((error, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="aiio-error-item">
                    <span>{buildErrorMessage(error)}</span>
                  </div>
                ))}
              </div>
            )}
          >
            <Tag color="red">{count}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('ORDER.IMPORT_ORDER.TYPE'),
      dataIndex: 'type',
      key: 'type',
      align: 'center',
      width: 72,
      ellipsis: true,
      render: (val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        return (
          <span className={hasFieldError(rowIndex, 'type') ? 'aiio-field-error' : ''}>
            {val || '—'}
          </span>
        );
      },
    },
    {
      title: t('COMMON.START_DATE'),
      dataIndex: 'startDate',
      key: 'startDate',
      width: 170,
      render: (val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        const rawStart = editedPreview[rowIndex]?.startDate ?? val;
        const pickerValue = rawStart ? moment.utc(rawStart).local() : null;
        return (
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            className="aiio-full-width"
            value={pickerValue}
            status={hasFieldError(rowIndex, 'startDate') ? 'error' : undefined}
            onChange={(date) => handleFieldChange(rowIndex, 'startDate', date ? date.toISOString() : null)}
          />
        );
      },
    },
    {
      title: t('COMMON.END_DATE'),
      dataIndex: 'endDate',
      key: 'endDate',
      width: 170,
      render: (val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        const rawEnd = editedPreview[rowIndex]?.endDate ?? val;
        const pickerValue = rawEnd ? moment.utc(rawEnd).local() : null;
        return (
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            className="aiio-full-width"
            value={pickerValue}
            status={hasFieldError(rowIndex, 'endDate') ? 'error' : undefined}
            onChange={(date) => handleFieldChange(rowIndex, 'endDate', date ? date.toISOString() : null)}
          />
        );
      },
    },
    {
      title: t('ORDER.VEHICLES'),
      dataIndex: 'vehicles',
      key: 'vehicles',
      width: 180,
      render: (val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        const aiList = Array.isArray(val)
          ? val.filter((vehicle) => vehicle && (vehicle.licensePlate || vehicle.countryCode))
          : [];
        const aiCount = aiList.length;

        const vehicleOptions = (allVehicles || []).map((vehicle) => {
          const rawPlate = vehicle?.licencePlateNumber || '';
          const cc = '';
          const plate = rawPlate;
          const value = `${cc}#${plate}`;
          return {
            value,
            label: plate || '—',
            _raw: { ...vehicle },
          };
        });

        const selectedPair = Array.isArray(editedPreview[rowIndex]?.vehicles)
          ? editedPreview[rowIndex].vehicles[0]
          : undefined;
        let selectedValue;
        if (selectedPair && (selectedPair.countryCode || selectedPair.licensePlate)) {
          const ccSel = (selectedPair.countryCode || '').toString().trim();
          const plateSel = (selectedPair.licensePlate || '').toString().trim();
          selectedValue = `${ccSel}#${plateSel}`;
        } else {
          selectedValue = undefined;
        }

        const optionsValueSet = new Set(vehicleOptions.map((option) => option.value));
        let safeSelectedValue = (selectedValue && optionsValueSet.has(selectedValue)) ? selectedValue : undefined;

        if (!safeSelectedValue && selectedPair) {
          const plateSel = (selectedPair.licensePlate || '').toString().trim().toLowerCase();
          const byPlate = vehicleOptions.find((option) => (
            (option?.label || '').toString().trim().toLowerCase() === plateSel
          ));
          safeSelectedValue = byPlate ? byPlate.value : undefined;
        }

        return (
          <div className="aiio-flex-row aiio-align-center aiio-gap-8">
            <Select
              className="aiio-minw-200"
              showSearch
              allowClear
              placeholder={t('ORDER.VEHICLES_PROMPT')}
              loading={vehiclesLoading}
              optionFilterProp="children"
              filterOption={(input, option) => (option?.children || '')
                .toString().toLowerCase().includes((input || '').toLowerCase())}
              value={safeSelectedValue}
              status={hasFieldError(rowIndex, 'vehicles') ? 'error' : undefined}
              onChange={(newVal, option) => {
                const chosen = option?.props?._raw;
                if (newVal && chosen) {
                  const plate = chosen?.licencePlateNumber || '';
                  handleFieldChange(rowIndex, 'vehicles', [{ licensePlate: plate, countryCode: null }]);
                } else {
                  handleFieldChange(rowIndex, 'vehicles', []);
                }
                handleFieldChange(rowIndex, 'vehicleId', undefined);
                handleFieldChange(rowIndex, 'vehicle', undefined);
                handleFieldChange(rowIndex, 'selectedVehicleIndex', undefined);
              }}
            >
              {vehicleOptions.map((option) => (
                <Select.Option key={option.value} value={option.value} _raw={option._raw}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            {showAiVehiclesTooltip && aiCount ? (
              <Tooltip
                title={(
                  <div className="aiio-vehicles-tooltip">
                    <div className="aiio-mb-16">
                      {t('ORDER.IMPORT_ORDER.AI_VEHICLES_INFO')}
                    </div>
                    {aiList.map((vehicle, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={idx} className="aiio-nowrap">
                        {`${vehicle.countryCode ? `${vehicle.countryCode} ` : ''}${vehicle.licensePlate || '—'}`.trim()}
                      </div>
                    ))}
                  </div>
                )}
              >
                <InfoCircleOutlined className="aiio-info-icon" />
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      title: t('ORDER.STOPS'),
      dataIndex: 'stops',
      key: 'stops',
      align: 'center',
      width: 56,
      render: (val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        const className = hasFieldError(rowIndex, 'stops') ? 'aiio-field-error' : '';
        const list = Array.isArray(val) ? val : [];
        const count = list.length;
        if (!count) return <span className={className}>0</span>;
        return (
          <span className={className}>
            {count}
            <Tooltip
              overlayClassName="aiio-stops-tooltip"
              title={(
                <div className="aiio-stops-list">
                  {list.map((stop, idx) => {
                    const address = stop?.place
                      ? [
                        stop.place.street,
                        stop.place.houseNumber,
                        stop.place.postcode,
                        stop.place.city,
                        stop.place.country,
                      ].filter(Boolean).join(', ')
                      : undefined;
                    const coords = (stop?.location
                      && typeof stop.location.latitude === 'number'
                      && typeof stop.location.longitude === 'number')
                      ? `${stop.location.latitude.toFixed(6)}, ${stop.location.longitude.toFixed(6)}`
                      : undefined;
                    const label = [
                      stop?.isLoad ? t('ORDER.IMPORT_ORDER.LOAD') : '',
                      stop?.isUnload ? t('ORDER.IMPORT_ORDER.UNLOAD') : '',
                    ].filter(Boolean).join(' ');
                    return (
                    // eslint-disable-next-line react/no-array-index-key
                      <div key={idx} className="aiio-stop-item">
                        <div className="aiio-fw-500">
                          {stop?.name || '—'}
                          {' '}
                          {label ? `(${label})` : ''}
                        </div>
                        <div className="aiio-nowrap">
                          {(() => {
                            const hasStart = !!stop?.startDate;
                            const hasEnd = !!stop?.endDate;
                            const startFmt = hasStart
                              ? moment.utc(stop.startDate).local().format('YYYY-MM-DD HH:mm')
                              : '—';
                            if (hasStart && hasEnd) {
                              const endFmt = moment.utc(stop.endDate).local().format('YYYY-MM-DD HH:mm');
                              if (startFmt === endFmt) {
                                return `${startFmt} → ${t('ORDER.IMPORT_ORDER.FIXED')}`;
                              }
                              return `${startFmt} → ${endFmt}`;
                            }
                            if (hasStart) return startFmt;
                            return '—';
                          })()}
                        </div>
                        {address && (
                          <div>
                            {address}
                          </div>
                        )}
                        {coords && (
                          <div>
                            {coords}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            >
              <InfoCircleOutlined className="aiio-info-icon" />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: t('ORDER.IMPORT_ORDER.DESCRIPTION'),
      dataIndex: 'description',
      key: 'description',
      width: 220,
      ellipsis: true,
      render: (val, record) => {
        const rowIndex = typeof record?.key === 'number' ? record.key : 0;
        return (
          <span className={hasFieldError(rowIndex, 'description') ? 'aiio-field-error' : ''}>
            {val || '—'}
          </span>
        );
      },
    },
  ]), [
    t,
    errorsByIndex,
    allVehicles,
    vehiclesLoading,
    editedPreview,
    showAiVehiclesTooltip,
    handleFieldChange,
    hasFieldError,
  ]);

  const rowErrorsCount = useMemo(() => (
    Array.from(errorsByIndex.values()).reduce((sum, list) => sum + list.length, 0)
  ), [errorsByIndex]);

  return (
    <>
      <Alert
        message={<b>{t('ORDER.IMPORT_ORDER.PREVIEW_TITLE')}</b>}
        description={t('ORDER.IMPORT_ORDER.PREVIEW_DESC')}
        type="info"
        showIcon
        className="aiio-mb-16"
      />
      {hasUnvalidatedChanges && (
        <Alert
          message={<b>{t('COMMON.WARNING')}</b>}
          description={t('ORDER.IMPORT_ORDER.REVALIDATION_REQUIRED_AFTER_EDITS')}
          type="warning"
          showIcon
          className="aiio-mb-16"
        />
      )}
      {!!fileLevelErrors.length && (
        <Alert
          message={<b>{t('ORDER.IMPORT_ORDER.FILE_ERRORS_TITLE')}</b>}
          description={(
            <div>
              <div>{t('ORDER.IMPORT_ORDER.FILE_ERRORS_DESC')}</div>
              <ul className="aiio-file-errors">
                {fileLevelErrors.map((error, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                  <li key={idx}>{buildErrorMessage(error)}</li>
                ))}
              </ul>
            </div>
          )}
          type="error"
          showIcon
          className="aiio-mb-16"
        />
      )}
      {!!rowErrorsCount && (
        <Alert
          message={<b>{t('COMMON.ERRORS')}</b>}
          description={(
            <div>
              {t('ORDER.IMPORT_ORDER.PREVIEW_ERRORS_DESC')}
              <br />
              <span className="aiio-fw-500">
                {t('ORDER.IMPORT_ORDER.PREVIEW_ERRORS_COUNT', { count: rowErrorsCount })}
              </span>
            </div>
          )}
          type="error"
          showIcon
          className="aiio-mb-16"
        />
      )}
      <Table
        dataSource={tableData}
        columns={columns}
        size="small"
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '20', '50'],
        }}
        onChange={handleTableChange}
        tableLayout="fixed"
        scroll={{ x: 1150 }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          getCheckboxProps: (record) => ({
            disabled: errorsByIndex.has(record?.key),
          }),
        }}
        rowClassName={(record) => (errorsByIndex.has(record?.key) ? 'table-row-error' : '')}
      />
    </>
  );
};

ImportOrderPreviewStep.propTypes = {
  previewState: PropTypes.shape({
    previewErrors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])).isRequired,
    errorsByIndex: PropTypes.shape({
      has: PropTypes.func.isRequired,
      get: PropTypes.func.isRequired,
      values: PropTypes.func.isRequired,
      forEach: PropTypes.func.isRequired,
    }).isRequired,
    fileLevelErrors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])).isRequired,
    editedPreview: PropTypes.arrayOf(PropTypes.shape({
      action: PropTypes.string,
      internalId: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      vehicles: PropTypes.arrayOf(PropTypes.shape({
        countryCode: PropTypes.string,
        licensePlate: PropTypes.string,
      })),
    })).isRequired,
    allVehicles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    vehiclesLoading: PropTypes.bool.isRequired,
    tableData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    selectedRowKeys: PropTypes.arrayOf(PropTypes.number).isRequired,
    hasUnvalidatedChanges: PropTypes.bool.isRequired,
    setSelectedRowKeys: PropTypes.func.isRequired,
    handleFieldChange: PropTypes.func.isRequired,
    handleTableChange: PropTypes.func.isRequired,
  }).isRequired,
  showAiVehiclesTooltip: PropTypes.bool,
};

ImportOrderPreviewStep.defaultProps = {
  showAiVehiclesTooltip: false,
};

export default ImportOrderPreviewStep;
